import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { LoadingButton } from '@mui/lab'

import PageContainer from '@/components/ui/containers/PageContainer'
import { PatientProfileForm } from '@/features/patient/patient-profile-form'
import { DMPConnectVitaleCardDialog, PlatformVitaleCardDialog, useSelectVitaleCardInfo, useVitaleCardDisplayLogic } from '@/widgets/vitale-card'
import { useIntro } from '@/hooks/useIntro'
import { CreatePatientIntro } from '@/utils/onboarding/intro'

import { CGUDialog } from './CGUDialog'
import { IngestExternalIdentityDialog } from './IngestExternalIdentityDialog'
import { useDialogState } from '../model/useDialogState'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

type CreatePatientPageProps = {}

export function CreatePatientPage(props: CreatePatientPageProps) {
  useLingui()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { showDMPConnectVitaleCard, showPlatformVitaleCard } = useVitaleCardDisplayLogic()
  const { closeDialog, dialogState, setDialogState } = useDialogState()
  const { isINSEnabled: isInsEnabled } = useIsINSEnabled()
  const introEnabled = useIntro(CreatePatientIntro, dialogState !== 'cgu')
  const { vitaleCardInfo, onDMPConnectVitaleCardSelect, onPlatformVitaleCardSelect } = useSelectVitaleCardInfo()
  const authUser = useAppSelector(selectAuthUser)
  const establishmentId =
    typeof authUser?.establishment === 'string' ? authUser.establishment : authUser?.establishment?._id

  return (
    <PageContainer>
      <Paper sx={{ mt: '15px', p: '30px' }}>
        <Stack direction="row" justifyContent="end" spacing={1}>
          {showDMPConnectVitaleCard && isInsEnabled && (
            <Button color="secondary" size="small" onClick={() => setDialogState('add-by-vitale-card')} variant="contained" id="carte-vitale">
              <Trans>Add by vitale card</Trans>
            </Button>
          )}
          {showPlatformVitaleCard && (
            <Button color="secondary" size="small" onClick={() => setDialogState('carte-vitale-infos')} variant="contained" id="carte-vitale">
              {t('page.createPatient.addPatientByCarteVitaleButton.label')}
            </Button>
          )}
          {isInsEnabled && (
            <Button
              color="secondary"
              size="small"
              variant="contained"
              onClick={() => setDialogState('add-by-external-identity')}
              id="add-by-external-identity"
            >
              <Trans>Receive an external identity</Trans>
            </Button>
          )}
        </Stack>
        <PatientProfileForm
          action={({ isSubmitting }) => (
            <Stack direction="row" justifyContent="end" spacing={2}>
              <Button variant="neutral" onClick={() => navigate('/patients')} type="button">
                <Trans>Cancel</Trans>
              </Button>
              <LoadingButton id="add-patient-button" disabled={dialogState === 'cgu'} disableElevation loading={isSubmitting} type="submit" variant="contained">
                <Trans>Add</Trans>
              </LoadingButton>
            </Stack>
          )}
          initialValues={vitaleCardInfo}
          intro={introEnabled}
          mode="create"
        />
      </Paper>
      <DMPConnectVitaleCardDialog
        open={dialogState === 'add-by-vitale-card'}
        onClose={closeDialog}
        title={<Trans>Add by vitale card</Trans>}
        onSelectPatient={(patient) => {
          onDMPConnectVitaleCardSelect(patient)
          closeDialog()
        }}
      />
      <PlatformVitaleCardDialog onClose={closeDialog} onSelect={onPlatformVitaleCardSelect} open={dialogState === 'carte-vitale-infos'} />
      <IngestExternalIdentityDialog
        open={dialogState === 'add-by-external-identity'}
        onClose={closeDialog}
        establishmentId={establishmentId}
        onVerified={onPlatformVitaleCardSelect}
        onUpdated={(patientId) => {
          closeDialog()
          navigate(`/patients/${patientId}`)
        }}
      />
      <CGUDialog open={dialogState === 'cgu'} onClose={closeDialog} />
    </PageContainer>
  )
}

export default CreatePatientPage
